// components/FeedCompositionInput.js
import React from 'react';
import { COMPONENTS } from '../fluid-properties';

const FeedCompositionInput = ({ composition, setComposition, conditions, setConditions }) => {
  // Helper function to update a specific component's value
  const handleCompositionChange = (componentName, value) => {
    const newValue = parseFloat(value) || 0;
    setComposition({
      ...composition,
      [componentName]: newValue
    });
  };

  // Helper function to update feed conditions
  const handleConditionChange = (condition, value) => {
    const newValue = parseFloat(value) || 0;
    
    // Convert flow rate from e3m³/d to kmol/h if that's what changed
    if (condition === 'flowRate') {
      // Standard conditions: 15°C, 101.325 kPa
      // 1 kmol of ideal gas at STP occupies about 23.645 m³
      // So e3m³/d ÷ 23.645 × 1000 ÷ 24 = kmol/h
      const kmolPerHour = (newValue * 1000) / (23.645 * 24);
      
      setConditions({
        ...conditions,
        [condition]: newValue,
        flowRateKmol: kmolPerHour
      });
    } else {
      setConditions({
        ...conditions,
        [condition]: newValue
      });
    }
  };

  // Calculate total to normalize
  const total = Object.values(composition).reduce((sum, val) => sum + val, 0);

  return (
    <div className="feed-composition-input">
      <div className="composition-table">
        <h3>Feed Composition (mol%)</h3>
        <div className="composition-header">
          <div className="component-name">Component</div>
          <div className="component-formula">Formula</div>
          <div className="component-value">Mol%</div>
          <div className="component-normalized">Normalized</div>
        </div>
        
        {Object.values(COMPONENTS).map(component => (
          <div className="composition-row" key={component.name}>
            <div className="component-name">{component.name}</div>
            <div className="component-formula">{component.formula}</div>
            <div className="component-value">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={composition[component.name] || 0}
                onChange={(e) => handleCompositionChange(component.name, e.target.value)}
              />
            </div>
            <div className="component-normalized">
              {((composition[component.name] || 0) / total * 100).toFixed(2)}%
            </div>
          </div>
        ))}
        
        <div className="composition-total">
          <div className="component-name">Total</div>
          <div className="component-formula"></div>
          <div className="component-value">{total.toFixed(2)}</div>
          <div className="component-normalized">100.00%</div>
        </div>
      </div>

      <div className="feed-conditions">
        <h3>Feed Conditions</h3>
        <div className="condition-row">
          <label>Temperature (°C)</label>
          <input
            type="number"
            step="0.1"
            value={conditions.temperature}
            onChange={(e) => handleConditionChange('temperature', e.target.value)}
          />
        </div>
        
        <div className="condition-row">
          <label>Pressure (bar)</label>
          <input
            type="number"
            step="0.1"
            min="0"
            value={conditions.pressure}
            onChange={(e) => handleConditionChange('pressure', e.target.value)}
          />
        </div>
        
        <div className="condition-row">
          <label>Flow Rate (e3m³/d at STP)</label>
          <input
            type="number"
            step="10"
            min="0"
            value={conditions.flowRate}
            onChange={(e) => handleConditionChange('flowRate', e.target.value)}
          />
          <div className="unit-conversion-display">
            ≈ {((conditions.flowRateKmol || 0).toFixed(0))} kmol/h
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedCompositionInput;